/**
 * @date 03/14/2017
 * @copyright 2016 Empower Retirement
 * @description
 *
 * Encapsulates the data model, state, and business logic for messages.
 *
 */

import _ from "lodash";

import ExternalLogger from "../../logger/ExternalLogger";

//-----------------------------------------------------------------------
// CONSTRUCTOR
//-----------------------------------------------------------------------

/**
 * The constructor's parameters are "injected" via AJS's IoC.
 *
 * @constructor
 * @type class
 */
const TransactionMessagesService = function ($q, $state, transactionMessagesFactory) {
    const logger = ExternalLogger.getInstance("TransactionMessagesService");

    //-----------------------------------------------------------------------
    // PRIVATE MEMBERS
    //-----------------------------------------------------------------------

    /**
     * Creates an array of UI state names from the views hash.
     *
     * @param {Object} target - The object hash containing the state names.
     * @param {String} statePropName - The state property name.
     * @return {Array}
     */
    const createStatesList = function (target, statePropName) {
        const result = [];
        const wrapper = function (array) {
            return function pluckState(currentValue) {
                array.push(target[currentValue][statePropName]);
            };
        };

        Object.keys(target).forEach(wrapper(result));
        return result;
    };

    //-----------------------------------------------------------------------
    // CLASS MEMBERS
    //-----------------------------------------------------------------------

    /**
     * List of transaction messages.
     * @type {String}
     */
    this.transactionMessages = null;

    /**
     * Represents the current.
     * @type {String}
     */
    this.functionCode = null;

    /**
     * List of views that can have transaction messages; lists both the function code and UI state
     * name for each view.
     *
     * NOTE: Add to this list for new states that should show the transaction messages.
     *
     * TODO: BMR: 03/14/2017: Add correct state names for:NON_QUAL_VIEW_DEFERRAL
     */
    this.VIEWS = {
        MY_CONTRIBUTIONS: {
            FUNCTION_CODE: "WNPYI",
            STATE: "accountDetails.deferrals.contributionType",
            LAYOUT: "right"
        },
        DISBURSEMENT_OR_WITHDRAWAL: {
            FUNCTION_CODE: "WNDIR",
            STATE: "accountDetails.withdrawal.request",
            LAYOUT: "right"
        },
        NON_QUAL_VIEW_DEFERRAL: {
            FUNCTION_CODE: "WNNQD",
            STATE: "TODO-NON_QUAL_VIEW_DEFERRAL",
            LAYOUT: "right"
        },
        NON_QUAL_ENROLLMENT_DE: {
            FUNCTION_CODE: "WNNQE",
            STATE: "nqEnrollmentDistributions",
            LAYOUT: "right"
        },
        NON_QUAL_ENROLL_DEFERRALS: {
            FUNCTION_CODE: "WNNQC",
            STATE: "nqEnrollmentDeferrals",
            LAYOUT: "right"
        },
        GUIDED_LOAN_REQUEST: {
            FUNCTION_CODE: "WNGLR",
            STATE: "accountDetails.loan.request",
            LAYOUT: "right"
        },
        NON_QUAL_ENROLLMENT_SUMMARY: {
            FUNCTION_CODE: "WNNQS",
            STATE: "nqEnrollmentSummary",
            LAYOUT: "right"
        },
        DISTRIBUTION_AMOUNT_PAGE: {
            FUNCTION_CODE: "WNWRA",
            STATE: "accountDetails.distribution.amount",
            LAYOUT: "bottom"
        },
        DISTRIBUTION_LANDING_PAGE: {
            FUNCTION_CODE: "WNWRL",
            STATE: "accountDetails.distribution.landing",
            LAYOUT: "bottom"
        },
        DEMIN_LANDING_PAGE: {
            FUNCTION_CODE: "WNWRL",
            STATE: "accountDetails.distribution.deminLanding",
            LAYOUT: "bottom"
        },
        DISTRIBUTION_HARDSHIP_PAGE: {
            FUNCTION_CODE: "WNSHW",
            STATE: "accountDetails.distribution.hardship",
            LAYOUT: "bottom"
        },
        DISTRIBUTION_REASONS_PAGE: {
            FUNCTION_CODE: "WNWRR",
            STATE: "accountDetails.distribution.reasons",
            LAYOUT: "bottom"
        },
        TRS_FLEX_ACCOUNT_DETAILS: {
            FUNCTION_CODE: "WNTRS",
            STATE: "accountDetails.accountOverviewTRS",
            LAYOUT: "bottom"
        },
        NON_QUAL_VIEW_MY_DISTRIBUTION: {
            FUNCTION_CODE: "WNQDS",
            STATE: "accountDetails.landing",
            LAYOUT: ""
        },
        MY_BENEFICIARIES_VIEW: {
            FUNCTION_CODE: "WNBEN",
            STATE: "accountDetails.beneficiaries.listBeneficiaries",
            LAYOUT: "bottom"
        },
        ADD_BENEFICIARIES_VIEW: {
            FUNCTION_CODE: "WNBEN",
            STATE: "accountDetails.beneficiaries.addBeneficiary",
            LAYOUT: "bottom"
        },
        ACC_OVERVIEW_HSA_PAGE: {
            FUNCTION_CODE: "WNHSA",
            STATE: "accountDetails.accountOverviewHSA",
            LAYOUT: "bottom"
        },
        ANNUITY_LANDING_PAGE: {
            FUNCTION_CODE: "WNWAL",
            STATE: "accountDetails.annuityrollovers",
            LAYOUT: "bottom"
        },
        ADDITIONAL_LOAN_PAYMENT_PAGE: {
            FUNCTION_CODE: "LALNP",
            STATE: "accountDetails.loan.additonalLoanPayments",
            LAYOUT: "bottom"
        },
        LOAN_REAMORTIZATION_PAGE: {
            FUNCTION_CODE: "LLRAP",
            STATE: "accountDetails.loan.changeexistingpayments",
            LAYOUT: "bottom"
        }
    };

    /**
     * List of UI state names that can possibly have transaction messages.
     */
    this.statesWithMessages = createStatesList(this.VIEWS, "STATE");

    /**
     * Determines if the transactional messages column should show.
     * @return {Boolean}
     */
    this.showMessagesColumn = function () {
        const showForState = this.statesWithMessages.join(",").indexOf($state.current.name) > -1;
        return showForState && !!this.transactionMessages;
    };

    /**
     * Returns the function code associated with the current view.
     * @return {String}
     */
    this.getTransactionMessageFunctionCode = function () {
        return getProperty(this.VIEWS, $state.current.name, "FUNCTION_CODE");
    };

    /**
     * Return layout position based on functionCode
     * @return {String}
     */
    this.getTransactionMessageLayoutPosition = function () {
        return getProperty(this.VIEWS, $state.current.name, "LAYOUT");
    };

    /**
     * Match the property by state name and return property value
     */
    const getProperty = function (viewObj, stateName, propToRetrieve) {
        let functionCode = "";
        let layout = "";
        switch (stateName) {
            case viewObj.MY_CONTRIBUTIONS.STATE:
                functionCode = viewObj.MY_CONTRIBUTIONS.FUNCTION_CODE;
                layout = viewObj.MY_CONTRIBUTIONS.LAYOUT;
                break;

            case viewObj.DISBURSEMENT_OR_WITHDRAWAL.STATE:
                functionCode = viewObj.DISBURSEMENT_OR_WITHDRAWAL.FUNCTION_CODE;
                layout = viewObj.DISBURSEMENT_OR_WITHDRAWAL.LAYOUT;
                break;

            case viewObj.NON_QUAL_VIEW_DEFERRAL.STATE:
                functionCode = viewObj.NON_QUAL_VIEW_DEFERRAL.FUNCTION_CODE;
                layout = viewObj.NON_QUAL_VIEW_DEFERRAL.LAYOUT;
                break;

            case viewObj.NON_QUAL_ENROLLMENT_DE.STATE:
                functionCode = viewObj.NON_QUAL_ENROLLMENT_DE.FUNCTION_CODE;
                layout = viewObj.NON_QUAL_ENROLLMENT_DE.LAYOUT;
                break;

            case viewObj.NON_QUAL_ENROLL_DEFERRALS.STATE:
                functionCode = viewObj.NON_QUAL_ENROLL_DEFERRALS.FUNCTION_CODE;
                layout = viewObj.NON_QUAL_ENROLL_DEFERRALS.LAYOUT;
                break;

            case viewObj.GUIDED_LOAN_REQUEST.STATE:
                functionCode = viewObj.GUIDED_LOAN_REQUEST.FUNCTION_CODE;
                layout = viewObj.GUIDED_LOAN_REQUEST.LAYOUT;
                break;

            case viewObj.NON_QUAL_ENROLLMENT_SUMMARY.STATE:
                functionCode = viewObj.NON_QUAL_ENROLLMENT_SUMMARY.FUNCTION_CODE;
                layout = viewObj.NON_QUAL_ENROLLMENT_SUMMARY.LAYOUT;
                break;

            case viewObj.DISTRIBUTION_AMOUNT_PAGE.STATE:
                functionCode = viewObj.DISTRIBUTION_AMOUNT_PAGE.FUNCTION_CODE;
                layout = viewObj.DISTRIBUTION_AMOUNT_PAGE.LAYOUT;
                break;

            case viewObj.DISTRIBUTION_LANDING_PAGE.STATE:
                functionCode = viewObj.DISTRIBUTION_LANDING_PAGE.FUNCTION_CODE;
                layout = viewObj.DISTRIBUTION_LANDING_PAGE.LAYOUT;
                break;

            case viewObj.DISTRIBUTION_HARDSHIP_PAGE.STATE:
                functionCode = viewObj.DISTRIBUTION_HARDSHIP_PAGE.FUNCTION_CODE;
                layout = viewObj.DISTRIBUTION_HARDSHIP_PAGE.LAYOUT;
                break;

            case viewObj.DEMIN_LANDING_PAGE.STATE:
                functionCode = viewObj.DEMIN_LANDING_PAGE.FUNCTION_CODE;
                layout = viewObj.DEMIN_LANDING_PAGE.LAYOUT;
                break;

            case viewObj.DISTRIBUTION_REASONS_PAGE.STATE:
                functionCode = viewObj.DISTRIBUTION_REASONS_PAGE.FUNCTION_CODE;
                layout = viewObj.DISTRIBUTION_REASONS_PAGE.LAYOUT;
                break;

            case viewObj.TRS_FLEX_ACCOUNT_DETAILS.STATE:
                functionCode = viewObj.TRS_FLEX_ACCOUNT_DETAILS.FUNCTION_CODE;
                layout = viewObj.TRS_FLEX_ACCOUNT_DETAILS.LAYOUT;
                break;

            case viewObj.NON_QUAL_VIEW_MY_DISTRIBUTION.STATE:
                functionCode = viewObj.NON_QUAL_VIEW_MY_DISTRIBUTION.FUNCTION_CODE;
                layout = viewObj.NON_QUAL_VIEW_MY_DISTRIBUTION.LAYOUT;
                break;

            case viewObj.ADD_BENEFICIARIES_VIEW.STATE:
            case viewObj.MY_BENEFICIARIES_VIEW.STATE:
                functionCode = viewObj.ADD_BENEFICIARIES_VIEW.FUNCTION_CODE;
                layout = viewObj.ADD_BENEFICIARIES_VIEW.LAYOUT;
                break;
            case viewObj.ACC_OVERVIEW_HSA_PAGE.STATE:
                functionCode = viewObj.ACC_OVERVIEW_HSA_PAGE.FUNCTION_CODE;
                layout = viewObj.ACC_OVERVIEW_HSA_PAGE.LAYOUT;
                break;
            case viewObj.ANNUITY_LANDING_PAGE.STATE:
                functionCode = viewObj.ANNUITY_LANDING_PAGE.FUNCTION_CODE;
                layout = viewObj.ANNUITY_LANDING_PAGE.LAYOUT;
                break;
            case viewObj.ADDITIONAL_LOAN_PAYMENT_PAGE.STATE:
                functionCode = viewObj.ADDITIONAL_LOAN_PAYMENT_PAGE.FUNCTION_CODE;
                layout = viewObj.ADDITIONAL_LOAN_PAYMENT_PAGE.LAYOUT;
                break;
            case viewObj.LOAN_REAMORTIZATION_PAGE.STATE:
                functionCode = viewObj.LOAN_REAMORTIZATION_PAGE.FUNCTION_CODE;
                layout = viewObj.LOAN_REAMORTIZATION_PAGE.LAYOUT;
                break;
        }

        if (propToRetrieve === "LAYOUT") return layout;
        else if (propToRetrieve === "FUNCTION_CODE") return functionCode;
        else return "";
    };

    this.resetMessage = function () {
        this.transactionMessages = null;
    };

    /**
     * Uses the factory as a delegate to the hit the server API. Saves the data
     * as a model value on the service.
     *
     * @returns {Promise}
     */
    this.getTransactionMessages = function (groupId, functionCode) {
        if (!groupId || !functionCode) {
            return $q.reject(
                new Error("Get dividend params requires valid `groupId` and `functionCode` fields.")
            );
        }

        /**
         * Handles the successful promise resolution of the delegate request and stores the data in this data model or
         * rejects the promise based on invalid data. Hopefully our delegate already checked
         * for bad data, but in the event that it didn't or wasn't as fine grained as necessary
         * we'll perform an additional check.
         *
         * @param {*} data - The data from the delegate that made the service request.
         * @returns {*} Either data or a rejected promise.
         */
        const getTransactionMessagesSuccess = function (result) {
            logger.debug("getTransactionMessagesSuccess()");

            if (!result) {
                return $q.reject(new Error("Invalid data. Data cannot be null or undefined."));
            } else {
                this.transactionMessages = _.isEmpty(result.data) ? null : result.data;
                return this.transactionMessages;
            }
        }.bind(this);

        /**
         * Handles the failed promise rejection of the delegate request and continues to
         * propagate that rejection for other members of the promise chain.
         *
         * @param {Error} fault - The error from the delegate that made the service request.
         * @returns {Promise} Rejected promise.
         */
        const getTransactionMessagesFault = function (fault) {
            logger.warn("getTransactionMessagesFault()");
            return $q.reject(fault);
        }.bind(this);

        logger.debug("getTransactionMessages()");

        return transactionMessagesFactory
            .getTransactionMessages(groupId, functionCode)
            .then(getTransactionMessagesSuccess, getTransactionMessagesFault);
    };

    logger.debug("Constructor()");
};

TransactionMessagesService.$inject = ["$q", "$state", "transactionMessagesFactory"];
export default TransactionMessagesService;
